import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragHandle as DragHandleIcon,
  PauseCircleFilled as PauseIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import {
  listPauseReasons,
  createPauseReason,
  updatePauseReason,
  deletePauseReason,
} from "../../services/ticketPauseService";

const PRESET_COLORS = [
  "#FF9800", "#F44336", "#E91E63", "#9C27B0", "#673AB7",
  "#3F51B5", "#2196F3", "#03A9F4", "#00BCD4", "#009688",
  "#4CAF50", "#8BC34A", "#CDDC39", "#FFC107", "#FF5722",
  "#795548", "#607D8B", "#9E9E9E",
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  colorDot: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "inline-block",
    border: "2px solid rgba(0,0,0,0.1)",
  },
  colorPicker: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: theme.spacing(1),
  },
  colorOption: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.2s",
    "&:hover": {
      transform: "scale(1.2)",
    },
  },
  colorOptionSelected: {
    border: "2px solid #000",
    transform: "scale(1.2)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
  },
  defaultChip: {
    marginLeft: theme.spacing(1),
  },
  inactiveRow: {
    opacity: 0.5,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  infoBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,152,0,0.1)" : "rgba(255,152,0,0.05)",
    borderLeft: "4px solid #FF9800",
    borderRadius: 4,
  },
}));

const PauseReasonsSettings = () => {
  const classes = useStyles();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState("#FF9800");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsDefault, setFormIsDefault] = useState(false);

  const loadReasons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPauseReasons(false); // incluir inativos
      setReasons(data);
    } catch (err) {
      toast.error("Erro ao carregar motivos de pausa");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReasons();
  }, [loadReasons]);

  const handleOpenCreate = () => {
    setEditingReason(null);
    setFormName("");
    setFormDescription("");
    setFormColor("#FF9800");
    setFormIsActive(true);
    setFormIsDefault(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (reason) => {
    setEditingReason(reason);
    setFormName(reason.name);
    setFormDescription(reason.description || "");
    setFormColor(reason.color || "#FF9800");
    setFormIsActive(reason.isActive);
    setFormIsDefault(reason.isDefault);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingReason(null);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        color: formColor,
        isActive: formIsActive,
        isDefault: formIsDefault,
      };

      if (editingReason) {
        await updatePauseReason(editingReason.id, payload);
        toast.success("Motivo atualizado!");
      } else {
        await createPauseReason(payload);
        toast.success("Motivo criado!");
      }

      handleCloseModal();
      loadReasons();
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao salvar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reason) => {
    if (!window.confirm(`Remover motivo "${reason.name}"?`)) return;

    try {
      await deletePauseReason(reason.id);
      toast.success("Motivo removido!");
      loadReasons();
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao remover";
      toast.error(msg);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PauseIcon style={{ color: "#FF9800" }} />
            Motivos de Pausa
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Gerencie os motivos disponíveis ao pausar um ticket.
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Novo Motivo
        </Button>
      </div>

      <div className={classes.infoBox}>
        <Typography variant="body2">
          Os motivos de pausa são exibidos quando um atendente pausa um ticket.
          O motivo marcado como <strong>padrão</strong> será pré-selecionado automaticamente.
          Motivos inativos não aparecem na lista de seleção.
        </Typography>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : reasons.length === 0 ? (
        <div className={classes.emptyState}>
          <PauseIcon style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }} />
          <Typography variant="h6" color="textSecondary">
            Nenhum motivo cadastrado
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Clique em "Novo Motivo" para adicionar o primeiro.
          </Typography>
        </div>
      ) : (
        <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>Cor</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell width={100} align="center">Status</TableCell>
                <TableCell width={100} align="center">Padrão</TableCell>
                <TableCell width={100} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reasons.map((reason) => (
                <TableRow
                  key={reason.id}
                  className={!reason.isActive ? classes.inactiveRow : ""}
                >
                  <TableCell>
                    <span
                      className={classes.colorDot}
                      style={{ backgroundColor: reason.color || "#FF9800" }}
                    />
                  </TableCell>
                  <TableCell>
                    <strong>{reason.name}</strong>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {reason.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={reason.isActive ? "Ativo" : "Inativo"}
                      style={{
                        backgroundColor: reason.isActive ? "#4CAF50" : "#9E9E9E",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {reason.isDefault && (
                      <Chip
                        size="small"
                        label="Padrão"
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(reason)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton size="small" onClick={() => handleDelete(reason)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de Criar/Editar */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReason ? "Editar Motivo de Pausa" : "Novo Motivo de Pausa"}
        </DialogTitle>
        <DialogContent>
          <div className={classes.form}>
            <TextField
              label="Nome"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ex: Aguardando cliente"
            />

            <TextField
              label="Descrição (opcional)"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Ex: Cliente precisa verificar informações antes de prosseguir"
            />

            <div>
              <Typography variant="subtitle2" gutterBottom>
                Cor
              </Typography>
              <div className={classes.colorPicker}>
                {PRESET_COLORS.map((color) => (
                  <div
                    key={color}
                    className={`${classes.colorOption} ${formColor === color ? classes.colorOptionSelected : ""}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormColor(color)}
                  />
                ))}
              </div>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formIsActive}
                      onChange={(e) => setFormIsActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Ativo"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formIsDefault}
                      onChange={(e) => setFormIsDefault(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Padrão"
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formName.trim()}
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {editingReason ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PauseReasonsSettings;
